import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, status } = useAuth();
  const t = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (status === "authenticated") return <Navigate to={location.state?.from?.pathname || "/sensors"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login({ email: email.trim().toLowerCase(), password });
      toast.success(t("common.toasts.welcomeBack"));
      navigate(location.state?.from?.pathname || "/sensors", { replace: true });
    } catch (err) {
      toast.error(extractApiError(err, t("auth.login.invalidCredentials")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative grid-background">
        <div className="absolute inset-0 bg-gradient-to-br from-background/40 via-background/70 to-background" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Logo />
          <div>
            <div className="mono-eyebrow mb-3">{t("auth.login.sideEyebrow")}</div>
            <h1 className="font-display text-4xl tracking-tight leading-tight max-w-md">
              {t("auth.login.sideTitle")}
            </h1>
            <p className="text-sm text-muted-foreground mt-4 max-w-md">
              {t("auth.login.sideDescription")}
            </p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="mono-eyebrow mb-2">{t("auth.login.eyebrow")}</div>
            <h2 className="font-display text-2xl tracking-tight">{t("auth.login.title")}</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs">{t("common.fields.email")}</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs">{t("common.fields.password")}</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          </div>
          <Button type="submit" className="w-full rounded-lg" disabled={busy} data-testid="login-submit-button">
            {busy ? t("auth.login.submitting") : t("auth.login.submit")}
          </Button>
          <div className="text-xs text-muted-foreground text-center">
            {t("auth.login.noOrganization")}{" "}
            <Link to="/register" className="text-foreground hover:underline" data-testid="login-register-link">
              {t("auth.login.createOne")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
